// src/Components/Common/PaginationBar.tsx
// Component phân trang dùng chung: nút chuyển trang dạng mũi tên, ở giữa là dải số trang.
// Presentational — nhận state/callback qua props, không tự đọc Redux, để dùng được ở cả
// những màn quản lý state qua Redux (UserManagement) lẫn state cục bộ (IssuancePlan).

import React from 'react';
import { Row, Col, Input, Pagination, PaginationItem, PaginationLink } from 'reactstrap';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('component_pagination_bar');

export interface PaginationBarProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  pageSize?: number;
  pageSizeOptions?: number[];
  onPageSizeChange?: (pageSize: number) => void;
}

// Tính dải số trang hiển thị, rút gọn bằng dấu "..." khi có nhiều trang
const getPageNumbers = (currentPage: number, totalPages: number): (number | string)[] => {
  const pages: (number | string)[] = [];
  const maxVisible = 5;

  if (totalPages <= maxVisible) {
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
  } else {
    pages.push(1);

    if (currentPage > 3) {
      pages.push('...');
    }

    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (currentPage < totalPages - 2) {
      pages.push('...');
    }

    pages.push(totalPages);
  }

  return pages;
};

const PaginationBar: React.FC<PaginationBarProps> = ({
  currentPage,
  totalPages,
  totalItems,
  onPageChange,
  pageSize,
  pageSizeOptions = [10, 20, 50, 100],
  onPageSizeChange,
}) => {
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  };

  if (totalItems === 0) {
    return null;
  }

  return (
    <Row className="align-items-center mt-3">
      {/* Bên trái: bộ chọn số bản ghi mỗi trang (nếu có) */}
      <Col sm={6}>
        {onPageSizeChange && pageSize !== undefined && (
          <div className="d-flex align-items-center">
            <span className="text-muted me-2">{st('hien_thi_text')}</span>
            <Input
              type="select"
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="form-select form-select-sm"
              style={{ width: 'auto' }}
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </Input>
            <span className="text-muted ms-2">{st('ban_ghi_text')}</span>
          </div>
        )}
      </Col>

      {/* Bên phải: mũi tên chuyển trang + dải số trang */}
      <Col sm={6}>
        <div className="d-flex justify-content-end">
          <Pagination className="pagination-separated mb-0">
            <PaginationItem disabled={currentPage === 1}>
              <PaginationLink previous onClick={() => handlePageChange(currentPage - 1)}>
                <i className="ri-arrow-left-line"></i>
              </PaginationLink>
            </PaginationItem>

            {getPageNumbers(currentPage, totalPages).map((page, index) => (
              <PaginationItem key={index} active={page === currentPage} disabled={page === '...'}>
                <PaginationLink onClick={() => typeof page === 'number' && handlePageChange(page)}>
                  {page}
                </PaginationLink>
              </PaginationItem>
            ))}

            <PaginationItem disabled={currentPage === totalPages}>
              <PaginationLink next onClick={() => handlePageChange(currentPage + 1)}>
                <i className="ri-arrow-right-line"></i>
              </PaginationLink>
            </PaginationItem>
          </Pagination>
        </div>
      </Col>
    </Row>
  );
};

export default PaginationBar;
